import React, {useMemo, useRef, useEffect, useState} from 'react'
import * as d3 from 'd3'
import Colors from '../../../utils/Colors.js'
import prefersReducedMotion from '../../utils/PrefersReducedMotion.js'
import {Card, P} from '../UI/index.js'

type DataArray = {value: number, name: string, color?: string}[]

interface BarChartProps {
    width: number
    height?: number
    barHeight: number
    data: DataArray
    dataPrefix?: string
    dataSuffix?: string
    divergingData?: DataArray
    divergingDataPrefix?: string
    divergingDataSuffix?: string
    dataBarColor?: string
    divergingDataBarColor?: string
    maxXAxis?: number
    activeXTick?: string
    onClickXTick?: (tick: string) => void
    highlightedXTick?: string
    showNameInShortBar?: boolean
    showGrid?: boolean
    animated?: boolean
    disableHover?: boolean
    sortYData?: (data: DataArray) => DataArray
}

/**
 * @param {BarChartProps} props
 *
 * @returns {React.JSX.Element} The rendered BarChart component
 */
export default function HorizontalBarChart({width, height, barHeight = 22, data, dataPrefix = '', dataSuffix = '', divergingData, divergingDataPrefix = '', divergingDataSuffix = '', dataBarColor, divergingDataBarColor, maxXAxis, activeXTick, onClickXTick, highlightedXTick, showNameInShortBar = true, showGrid = true, animated = !prefersReducedMotion, disableHover = false, sortYData}: BarChartProps): React.JSX.Element {
    const highlightedXTickRect = useRef<SVGGElement>()
    const [hoverXTick, setHoverXTick] = useState<string | null>(null)
    const margin = {top: 20, right: 30, bottom: 30, left: width * 0.3}
    const barSpacing = 16
    const ownHeight = Math.ceil(([...data, ...(divergingData || [])].length + 0.1) * (barHeight + barSpacing)) + margin.top + margin.bottom

    if (!height) {
        height = ownHeight
    } else if (height < ownHeight) {
        height = ownHeight
    }

    const svgRef = useRef(null)

    const dataIsEmpty = useMemo(() => {
        return data.length === 0 && (!divergingData || divergingData.length === 0)
    }, [data, divergingData])

    useEffect(() => {
        if (svgRef.current) {
            const svg = d3.select(svgRef.current)
            const count = svg.selectAll('.bar').size()
            const duration = 750
            const delay = 50
            const totalDur = Math.min((duration + (count - 1) * delay), 1_000) // max duration of 1 second
            const bars = svg.selectAll('.bar')
            const labels = bars.select((_, i, bars) => {
                const el = bars[i] as Element
                return el.nextElementSibling
            })

            if (animated) {
                const delayDurPerBar = totalDur / bars.size()

                labels.attr('x', (_, i, labels) => {
                    const el = labels[i]
                    if (!el) {
                        return null
                    }

                    const currentX = el.getAttribute('x')

                    if (currentX) {
                        return currentX
                    }

                    const value = el.getAttribute('data-value')

                    if (!value) {
                        return null
                    }

                    return xScale(parseFloat(value)) - xScale(minimumX)
                })

                bars.transition()
                    .duration(duration)
                    .delay((_, i) => i * delayDurPerBar)
                    .attr('width', (_, i, bars) => {
                        const el = bars[i] as Element
                        if (!el) {
                            return null
                        }

                        const value = el.getAttribute('data-value')

                        if (!value) {
                            return null
                        }

                        return xScale(parseFloat(value)) - xScale(minimumX)
                    })
                    .each((_, i, bars) => {
                        const el = bars[i] as Element

                        if (!el) {
                            return null
                        }

                        const value = el.getAttribute('data-value')

                        if (!value) {
                            return null
                        }

                        const label = d3.select(el.nextElementSibling)
                        label.transition()
                            .duration(duration)
                            .attr('x', (_, i, labels) => {
                                const el = labels[i]

                                const isDiverging = el?.getAttribute('data-is-divergent')

                                if (isDiverging) {
                                    return 0
                                }

                                return xScale(parseFloat(value)) - xScale(minimumX)
                            })

                        label.transition()
                            .duration(200)
                            .ease(d3.easeCubicInOut)
                            .delay(totalDur)
                            .style('opacity', 1)
                    })
            } else {
                bars.attr('width', (_, i, bars) => {
                    const el = bars[i] as Element

                    if (!el) {
                        return null
                    }

                    const value = el.getAttribute('data-value')

                    if (!value) {
                        return null
                    }

                    return xScale(parseFloat(value)) - xScale(minimumX)
                })

                labels.attr('x', (_, i, labels) => {
                    const el = labels[i] as Element

                    if (!el) {
                        return null
                    }

                    const value = el.getAttribute('data-value')

                    if (!value) {
                        return null
                    }

                    return xScale(parseFloat(value)) - xScale(minimumX)
                })
            }
        }
    }, [data, margin.left])

    useEffect(() => {
        if (highlightedXTick) {
            setTimeout(() => {
                if (highlightedXTickRect.current) {
                    highlightedXTickRect.current.style.scrollMarginTop = '100px'
                    highlightedXTickRect.current?.scrollIntoView({block: 'start'})
                    highlightedXTickRect.current.style.scrollMarginTop = ''
                }
            }, 1000)
        }
    }, [highlightedXTick, highlightedXTickRect.current])

    const minimumX = dataIsEmpty && divergingData ? -100 : 0

    const dataMax = d3.max(data, (d) => d.value) || 0

    const roundingFactor = Math.pow(10, Math.floor(Math.log10(dataMax)))

    const maximumX = dataIsEmpty ? 100 : Math.ceil(dataMax / roundingFactor) * roundingFactor

    const xScale = useMemo(() => {
        if (divergingData) {
            let max = dataIsEmpty ? 100 : Math.max(d3.max(divergingData, (d) => d.value) || 0, d3.max(data, (d) => d.value) || 0)
            if (maxXAxis && (max > maxXAxis)) {
                max = maxXAxis
            }
            return d3.scaleLinear()
                .domain([-Math.abs(max), max])
                .range([margin.left, (width) - margin.right])
        } else {
            return d3.scaleLinear()
                .domain([minimumX, maximumX])
                .range([margin.left, (width) - margin.right])
        }
    }, [data, divergingData, width, margin.left])

    const yScale = useMemo(() => {
        const domainData = sortYData ? sortYData([...data, ...(divergingData || [])]) : [...data, ...(divergingData || [])]
        return d3.scaleBand()
            .domain(domainData.map((d) => d.name))
            .range([margin.top, (ownHeight - margin.bottom)])
            .padding(0.1)
    }, [data, divergingData, ownHeight, sortYData])

    const format = (value: number) => {
        return `${value > 0 ? dataPrefix : value === 0 ? '' : divergingDataPrefix}${Math.abs(value).toLocaleString('nl-NL')}${value > 0 ? dataSuffix : value === 0 ? '' : divergingDataSuffix}`
    }

    const xTicks = xScale.ticks(maximumX < 5 ? maximumX : 5)

    const xTickElements = xTicks.map((tick) => (
        <g key={tick} transform={`translate(${xScale(tick)}, 0)`}>
            <text y={0} dy='.9em' textAnchor='middle'>{format(tick)}</text>
        </g>
    ))

    const yTicks = yScale.domain().map((tick) => {
        let displayTick = tick

        if (tick.includes('Verwijderde koerier')) {
            displayTick = tick.split('_')[0]
        }

        return (
            <g
                key={tick}
                style={!disableHover ? {cursor: 'pointer'} : {}}
                onMouseEnter={() => setHoverXTick(tick)}
                onMouseLeave={() => setHoverXTick(null)}
                onClick={(onClickXTick && !disableHover) ? () => onClickXTick(tick) : undefined}
                data-value={tick}
                transform={`translate(0, ${(yScale(tick) || 0) + (yScale.bandwidth() - barSpacing) / 2})`}
            >
                <foreignObject height={20} width={width} x={0} y='-.6em'>
                    <P title={displayTick} style={{color: Colors.black, width: xScale(xScale.domain()[0]) - 20, fontWeight: highlightedXTick === tick ? 'bold' : 'normal'}} ellipsis>{displayTick}</P>
                </foreignObject>
            </g>
        )
    })

    return (
        <>
            {dataIsEmpty &&
                <Card style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', padding: 24}}>
                    Geen data gevonden in de geselecteerde periode.
                </Card>
            }
            <svg style={{height: '100%'}} ref={svgRef} width={width} height={height - 4} viewBox={`0 0 ${width} ${height}`}>
                {activeXTick &&
                    <g transform={`translate(0, ${(yScale(activeXTick) || 0) + (yScale.bandwidth() - barSpacing) / 2})`}>
                        <rect
                            fill='rgba(0, 0, 0, 0.1)'
                            width={xScale(maximumX)}
                            height={24}
                            y='-.7em'
                        />
                    </g>
                }
                {(highlightedXTick || (hoverXTick && !disableHover)) &&
                    <g transform={`translate(0, ${(yScale(highlightedXTick || hoverXTick) || 0) + (yScale.bandwidth() - barSpacing) / 2})`} ref={highlightedXTickRect}>
                        <rect
                            fill='rgba(0, 0, 0, 0.1)'
                            width={xScale(xScale.domain()[1])}
                            height={24}
                            y='-.7em'
                        />
                    </g>
                }
                <g>
                    {showGrid &&
                        <g>
                            {/* x-axis vertical lines */}
                            <g>
                                <line x1={xScale(minimumX)} x2={xScale(minimumX)} y1={height - margin.bottom} y2={margin.top} stroke={Colors.grey30} strokeWidth={1} />
                                {
                                    xTicks.map((tick) => (
                                        <line key={tick} x1={xScale(tick)} x2={xScale(tick)} y1={height - margin.bottom} y2={margin.top} stroke={Colors.grey30} strokeWidth={1} />
                                    ))
                                }
                            </g>
                            {/* y-axis horizontal lines */}
                            <g>
                                <line x1={xScale(xTicks[0])} x2={xScale(xTicks[xTicks.length - 1])} y1={margin.top} y2={margin.top} stroke={Colors.grey30} strokeWidth={1} />
                                <line x1={xScale(xTicks[0])} x2={xScale(xTicks[xTicks.length - 1])} y1={height - margin.bottom} y2={height - margin.bottom} stroke={Colors.grey30} strokeWidth={1} />
                            </g>
                        </g>
                    }
                    {data.map((d, i) => {
                        const shortBar = xScale(d.value) - xScale(minimumX) < 100

                        const trimmedValue = maxXAxis ? Math.min(d.value, maxXAxis) : d.value

                        return (
                            <g key={i} transform={`translate(${xScale(minimumX)}, ${yScale(d.name)})`}>
                                <rect
                                    fill={d.color || dataBarColor || '#D1D1D1'}
                                    width={0}
                                    height={yScale.bandwidth() - barSpacing}
                                    className='bar'
                                    data-value={trimmedValue}
                                />
                                <text
                                    y={(yScale.bandwidth() - barSpacing) / 2}
                                    dy='0.35em'
                                    dx={shortBar ? 16 : -12}
                                    fill={shortBar ? Colors.textMedium : Colors.textLight}
                                    textAnchor={shortBar ? 'start' : 'end'}
                                    style={{opacity: (!animated) ? 1 : 0}}
                                    className='bar-label'
                                    data-value={d.value}
                                >
                                    {showNameInShortBar ? `${d.name} - ` : ''}{format(d.value)}
                                </text>
                            </g>
                        )
                    })}
                    {divergingData && divergingData.length > 0 &&
                        <g>
                            {divergingData.map((d, i) => {
                                const shortBar = Math.abs(xScale(d.value) - xScale(minimumX)) < 100

                                const trimmedValue = maxXAxis ? Math.min(d.value, maxXAxis) : d.value

                                return (
                                    <g key={i} transform={`translate(${xScale(minimumX) - (xScale(trimmedValue) - xScale(minimumX))}, ${yScale(d.name)})`}>
                                        <rect
                                            fill={d.color || divergingDataBarColor || '#D9534F'}
                                            width={0}
                                            height={yScale.bandwidth() - barSpacing}
                                            className='bar'
                                            data-value={trimmedValue}
                                            data-is-divergent
                                        />
                                        <text
                                            y={(yScale.bandwidth() - barSpacing) / 2}
                                            dy='0.35em'
                                            dx={d.value < 0 ? (shortBar ? -16 : 12) : (shortBar ? -16 : 12)}
                                            fill={shortBar ? Colors.textMedium : Colors.textLight}
                                            textAnchor={shortBar ? (d.value < 0 ? 'end' : 'end') : (d.value < 0 ? 'start' : 'start')}
                                            style={{opacity: (!animated) ? 1 : 0}}
                                            className='bar-label'
                                            data-value={d.value}
                                            data-is-divergent
                                        >
                                            {showNameInShortBar ? `${d.name} - ` : ''}{format(-d.value)}
                                        </text>
                                    </g>
                                )
                            })}
                        </g>
                    }
                    <g>
                        {xTickElements}
                    </g>
                    <g>
                        {yTicks}
                    </g>
                </g>
            </svg>

        </>
    )
}
